import React, { useState } from 'react';
import { Play, Pause, RotateCcw, Zap, Move, Scale, RotateCw, Compass, Mountain, Target, Download } from 'lucide-react';

export const MotionAnimationPrinciples: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'principles' | 'timing' | 'easing' | 'patterns'>('principles');
  const [isAnimating, setIsAnimating] = useState(false);

  const AnimatedExample: React.FC<{
    title: string;
    description: string;
    children: React.ReactNode;
    animationClass: string;
    duration: string;
  }> = ({ title, description, children, animationClass, duration }) => (
    <div className="bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-semibold text-charcoal-900">{title}</h3>
          <p className="text-sm text-charcoal-600">{description}</p>
        </div>
        <div className="text-right">
          <div className="text-sm font-mono bg-[var(--hover-tint)] px-2 py-1 rounded">{duration}</div>
        </div>
      </div>
      
      <div className="bg-[var(--surface)] rounded p-4 h-24 flex items-center justify-center relative overflow-hidden">
        <div className={`${animationClass} ${isAnimating ? 'animate' : ''}`}>
          {children}
        </div>
      </div>
      
      <div className="flex gap-2 mt-3">
        <button 
          onClick={() => setIsAnimating(!isAnimating)}
          className="flex items-center gap-1 px-3 py-1 text-xs bg-[var(--mustard-dim)] text-[var(--mustard)] rounded hover:bg-[var(--mustard-dim)] transition-colors"
        >
          {isAnimating ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
          {isAnimating ? 'Pause' : 'Play'}
        </button>
        <button 
          onClick={() => setIsAnimating(false)}
          className="flex items-center gap-1 px-3 py-1 text-xs bg-[var(--hover-tint)] text-[var(--text-sec)] rounded hover:bg-[var(--hover-tint)] transition-colors"
        >
          <RotateCcw className="h-3 w-3" />
          Reset
        </button>
      </div>
    </div>
  );

  const TimingExample: React.FC<{
    name: string;
    duration: string;
    description: string;
    usage: string;
  }> = ({ name, duration, description, usage }) => (
    <div className="bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-charcoal-900">{name}</h3>
        <div className="text-sm font-mono bg-[var(--hover-tint)] px-2 py-1 rounded">{duration}</div>
      </div>
      <p className="text-sm text-charcoal-600 mb-2">{description}</p>
      <p className="text-xs text-charcoal-500">{usage}</p>
    </div>
  );

  const EasingExample: React.FC<{
    name: string;
    curve: string;
    description: string;
    usage: string;
  }> = ({ name, curve, description, usage }) => (
    <div className="bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-charcoal-900">{name}</h3>
        <div className="text-sm font-mono bg-[var(--hover-tint)] px-2 py-1 rounded">{curve}</div>
      </div>
      <p className="text-sm text-charcoal-600 mb-2">{description}</p>
      <p className="text-xs text-charcoal-500">{usage}</p>
    </div>
  );

  const PatternExample: React.FC<{
    title: string;
    description: string;
    children: React.ReactNode;
    patternType: string;
  }> = ({ title, description, children, patternType }) => (
    <div className="bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-4">
      <div className="mb-3">
        <h3 className="font-semibold text-charcoal-900">{title}</h3>
        <p className="text-sm text-charcoal-600">{description}</p>
        <div className="text-xs text-charcoal-500 mt-1">Pattern: {patternType}</div>
      </div>
      
      <div className="bg-[var(--surface)] rounded p-4 h-32 flex items-center justify-center relative overflow-hidden">
        {children}
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center gap-3 mb-2">
          <Zap className="h-10 w-10 text-[var(--mustard)]" />
          <h1 className="text-4xl font-bold text-charcoal-900">Motion & Animation Principles</h1>
        </div>
        <p className="text-lg text-charcoal-600 max-w-3xl mx-auto">
          Brand-appropriate animation guidelines for element transitions, entrance/exit patterns, and micro-interactions that reinforce Peak Priorities' clarity and precision.
        </p>
      </div>

      {/* System Overview */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <div className="flex items-center gap-3 mb-4">
          <Move className="h-6 w-6 text-[var(--mustard)]" />
          <h2 className="text-2xl font-bold text-charcoal-900">Motion System Overview</h2>
        </div>
        <p className="text-charcoal-700 leading-relaxed mb-6">
          Our motion system reflects the brand's core values of clarity, precision, and forward momentum. 
          Every animation should feel purposeful and guide users toward their goals, just like a compass pointing toward the summit.
        </p>
        
        <div className="grid md:grid-cols-4 gap-4">
          <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
            <h3 className="font-bold text-[var(--mustard)] mb-2">Purposeful</h3>
            <p className="text-sm text-[var(--mustard)]">Every animation serves a clear purpose and enhances understanding</p>
          </div>
          <div className="bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-200 p-4">
            <h3 className="font-bold text-consulting-slate-blue-900 mb-2">Precise</h3>
            <p className="text-sm text-consulting-slate-blue-800">Timing and easing are carefully calibrated for optimal feel</p>
          </div>
          <div className="bg-sage-50 border-2 border-sage-200 p-4">
            <h3 className="font-bold text-sage-900 mb-2">Consistent</h3>
            <p className="text-sm text-sage-800">Motion patterns are consistent across all touchpoints</p>
          </div>
          <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
            <h3 className="font-bold text-[var(--mustard)] mb-2">Accessible</h3>
            <p className="text-sm text-[var(--mustard)]">Respects user preferences and accessibility needs</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex justify-center gap-4">
        <button
          onClick={() => setActiveTab('principles')}
          className={`
            flex items-center gap-2 px-6 py-3  font-semibold transition-colors
            ${activeTab === 'principles'
              ? 'bg-[var(--mustard)] text-white'
              : 'bg-[var(--hover-tint)] text-charcoal-700 hover:bg-[var(--hover-tint)]'
            }
          `}
        >
          <Zap className="h-5 w-5" />
          Principles
        </button>
        <button
          onClick={() => setActiveTab('timing')}
          className={`
            flex items-center gap-2 px-6 py-3  font-semibold transition-colors
            ${activeTab === 'timing'
              ? 'bg-[var(--mustard)] text-white'
              : 'bg-[var(--hover-tint)] text-charcoal-700 hover:bg-[var(--hover-tint)]'
            }
          `}
        >
          <Scale className="h-5 w-5" />
          Timing
        </button>
        <button
          onClick={() => setActiveTab('easing')}
          className={`
            flex items-center gap-2 px-6 py-3  font-semibold transition-colors
            ${activeTab === 'easing'
              ? 'bg-[var(--mustard)] text-white'
              : 'bg-[var(--hover-tint)] text-charcoal-700 hover:bg-[var(--hover-tint)]'
            }
          `}
        >
          <RotateCw className="h-5 w-5" />
          Easing
        </button>
        <button
          onClick={() => setActiveTab('patterns')}
          className={`
            flex items-center gap-2 px-6 py-3  font-semibold transition-colors
            ${activeTab === 'patterns'
              ? 'bg-[var(--mustard)] text-white'
              : 'bg-[var(--hover-tint)] text-charcoal-700 hover:bg-[var(--hover-tint)]'
            }
          `}
        >
          <Move className="h-5 w-5" />
          Patterns
        </button>
      </div>

      {/* Animation Principles */}
      {activeTab === 'principles' && (
        <div className="space-y-8">
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
            <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Core Animation Principles</h2>
            <p className="text-charcoal-700 mb-6">
              These principles guide all motion design decisions and ensure animations feel cohesive with the Peak Priorities brand.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <AnimatedExample
                title="Fade In"
                description="Gentle entrance for new content"
                animationClass="fade-in"
                duration="300ms"
              >
                <div className="w-12 h-12 bg-[var(--mustard)] rounded-full flex items-center justify-center">
                  <Compass className="h-6 w-6 text-white" />
                </div>
              </AnimatedExample>

              <AnimatedExample
                title="Slide Up"
                description="Content appearing from below"
                animationClass="slide-up"
                duration="400ms"
              >
                <div className="w-12 h-12 bg-consulting-slate-blue-600 rounded-full flex items-center justify-center">
                  <Mountain className="h-6 w-6 text-white" />
                </div>
              </AnimatedExample>

              <AnimatedExample
                title="Scale In"
                description="Elements growing into view"
                animationClass="scale-in"
                duration="250ms"
              >
                <div className="w-12 h-12 bg-sage-600 rounded-full flex items-center justify-center">
                  <Target className="h-6 w-6 text-white" />
                </div>
              </AnimatedExample>

              <AnimatedExample
                title="Rotate In"
                description="Compass-like rotation entrance"
                animationClass="rotate-in"
                duration="500ms"
              >
                <div className="w-12 h-12 bg-coral-600 rounded-full flex items-center justify-center">
                  <Compass className="h-6 w-6 text-white" />
                </div>
              </AnimatedExample>
            </div>
          </div>

          {/* Brand-Specific Animations */}
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
            <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Brand-Specific Animations</h2>
            <p className="text-charcoal-700 mb-6">
              Unique animations that reinforce the Peak Priorities brand identity and navigation metaphor.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <AnimatedExample
                title="Compass Rotation"
                description="Navigation and direction changes"
                animationClass="compass-rotate"
                duration="600ms"
              >
                <div className="w-16 h-16 border-2 border-[var(--mustard)] rounded-full flex items-center justify-center relative">
                  <div className="w-1 h-8 bg-[var(--mustard)] absolute transform origin-bottom compass-needle"></div>
                  <div className="w-2 h-2 bg-[var(--mustard)] rounded-full"></div>
                </div>
              </AnimatedExample>

              <AnimatedExample
                title="Mountain Ascent"
                description="Progress and achievement indicators"
                animationClass="mountain-ascent"
                duration="800ms"
              >
                <div className="flex items-end gap-1">
                  <div className="w-4 h-8 bg-sage-600 rounded-t mountain-peak"></div>
                  <div className="w-4 h-12 bg-sage-600 rounded-t mountain-peak"></div>
                  <div className="w-4 h-6 bg-sage-600 rounded-t mountain-peak"></div>
                </div>
              </AnimatedExample>

              <AnimatedExample
                title="Trail Dots"
                description="Progress and path indicators"
                animationClass="trail-dots"
                duration="1000ms"
              >
                <div className="flex gap-2">
                  <div className="w-3 h-3 bg-[var(--mustard)] rounded-full trail-dot"></div>
                  <div className="w-3 h-3 bg-[var(--mustard)] rounded-full trail-dot"></div>
                  <div className="w-3 h-3 bg-[var(--mustard)] rounded-full trail-dot"></div>
                  <div className="w-3 h-3 bg-[var(--mustard)] rounded-full trail-dot"></div>
                </div>
              </AnimatedExample>

              <AnimatedExample
                title="Target Focus"
                description="Selection and focus states"
                animationClass="target-focus"
                duration="300ms"
              >
                <div className="w-16 h-16 border-2 border-consulting-slate-blue-600 rounded-full flex items-center justify-center relative">
                  <div className="w-8 h-8 border border-consulting-slate-blue-600 rounded-full"></div>
                  <div className="w-4 h-4 bg-consulting-slate-blue-600 rounded-full absolute"></div>
                </div>
              </AnimatedExample>
            </div>
          </div>
        </div>
      )}

      {/* Timing System */}
      {activeTab === 'timing' && (
        <div className="space-y-8">
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
            <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Timing System</h2>
            <p className="text-charcoal-700 mb-6">
              Consistent timing creates predictable and comfortable user experiences. Our timing system is based on human perception and interaction patterns.
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <TimingExample
                name="Instant"
                duration="0ms"
                description="Immediate feedback for user actions"
                usage="Hover states, focus indicators, immediate responses"
              />
              <TimingExample
                name="Fast"
                duration="150ms"
                description="Quick transitions for micro-interactions"
                usage="Button hover, icon changes, small state changes"
              />
              <TimingExample
                name="Standard"
                duration="300ms"
                description="Default timing for most animations"
                usage="Page transitions, modal appearances, content changes"
              />
              <TimingExample
                name="Slow"
                duration="500ms"
                description="Deliberate timing for emphasis"
                usage="Loading states, major transitions, brand animations"
              />
              <TimingExample
                name="Deliberate"
                duration="800ms"
                description="Slow timing for dramatic effect"
                usage="Hero animations, major state changes, storytelling"
              />
              <TimingExample
                name="Extended"
                duration="1200ms"
                description="Long timing for complex animations"
                usage="Onboarding sequences, complex state changes, brand moments"
              />
            </div>
          </div>

          {/* Timing Guidelines */}
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
            <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Timing Guidelines</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Interaction Timing</h3>
                <div className="space-y-4">
                  <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
                    <h4 className="font-medium text-[var(--mustard)] mb-2">Hover Effects</h4>
                    <p className="text-sm text-[var(--mustard)]">150ms for immediate feedback without delay</p>
                  </div>
                  
                  <div className="bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-200 p-4">
                    <h4 className="font-medium text-consulting-slate-blue-900 mb-2">Click Feedback</h4>
                    <p className="text-sm text-consulting-slate-blue-800">100ms for instant visual confirmation</p>
                  </div>
                  
                  <div className="bg-sage-50 border-2 border-sage-200 p-4">
                    <h4 className="font-medium text-sage-900 mb-2">Page Transitions</h4>
                    <p className="text-sm text-sage-800">300ms for smooth navigation without feeling slow</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Content Timing</h3>
                <div className="space-y-4">
                  <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
                    <h4 className="font-medium text-[var(--mustard)] mb-2">Loading States</h4>
                    <p className="text-sm text-[var(--mustard)]">500ms for perceived performance improvement</p>
                  </div>
                  
                  <div className="bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-200 p-4">
                    <h4 className="font-medium text-consulting-slate-blue-900 mb-2">Modal Appearances</h4>
                    <p className="text-sm text-consulting-slate-blue-800">300ms for smooth overlay transitions</p>
                  </div>
                  
                  <div className="bg-sage-50 border-2 border-sage-200 p-4">
                    <h4 className="font-medium text-sage-900 mb-2">Brand Animations</h4>
                    <p className="text-sm text-sage-800">800ms for memorable brand moments</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Easing System */}
      {activeTab === 'easing' && (
        <div className="space-y-8">
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
            <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Easing System</h2>
            <p className="text-charcoal-700 mb-6">
              Easing curves define how animations accelerate and decelerate, creating natural and comfortable motion that feels human.
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <EasingExample
                name="Ease Out"
                curve="cubic-bezier(0.25, 0.46, 0.45, 0.94)"
                description="Fast start, slow finish - most natural feeling"
                usage="Default for most UI animations, page transitions"
              />
              <EasingExample
                name="Ease In"
                curve="cubic-bezier(0.55, 0.06, 0.68, 0.19)"
                description="Slow start, fast finish - builds anticipation"
                usage="Loading animations, content appearing"
              />
              <EasingExample
                name="Ease In Out"
                curve="cubic-bezier(0.42, 0, 0.58, 1)"
                description="Slow start and finish - smooth and elegant"
                usage="Modal animations, complex transitions"
              />
              <EasingExample
                name="Linear"
                curve="linear"
                description="Constant speed - mechanical feeling"
                usage="Progress bars, loading indicators"
              />
              <EasingExample
                name="Bounce"
                curve="cubic-bezier(0.68, -0.55, 0.265, 1.55)"
                description="Playful bounce effect - attention-grabbing"
                usage="Success states, celebrations, playful interactions"
              />
              <EasingExample
                name="Elastic"
                curve="cubic-bezier(0.175, 0.885, 0.32, 1.275)"
                description="Stretchy elastic effect - dynamic feeling"
                usage="Brand animations, dramatic entrances"
              />
            </div>
          </div>

          {/* Easing Applications */}
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
            <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Easing Applications</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-charcoal-900 mb-4">UI Interactions</h3>
                <div className="space-y-4">
                  <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
                    <h4 className="font-medium text-[var(--mustard)] mb-2">Button Hover</h4>
                    <p className="text-sm text-[var(--mustard)]">Ease out for natural feel</p>
                  </div>
                  
                  <div className="bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-200 p-4">
                    <h4 className="font-medium text-consulting-slate-blue-900 mb-2">Form Validation</h4>
                    <p className="text-sm text-consulting-slate-blue-800">Ease in for attention-grabbing</p>
                  </div>
                  
                  <div className="bg-sage-50 border-2 border-sage-200 p-4">
                    <h4 className="font-medium text-sage-900 mb-2">Navigation</h4>
                    <p className="text-sm text-sage-800">Ease in-out for smooth transitions</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Brand Moments</h3>
                <div className="space-y-4">
                  <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
                    <h4 className="font-medium text-[var(--mustard)] mb-2">Logo Animation</h4>
                    <p className="text-sm text-[var(--mustard)]">Elastic for memorable brand moments</p>
                  </div>
                  
                  <div className="bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-200 p-4">
                    <h4 className="font-medium text-consulting-slate-blue-900 mb-2">Success States</h4>
                    <p className="text-sm text-consulting-slate-blue-800">Bounce for celebratory feel</p>
                  </div>
                  
                  <div className="bg-sage-50 border-2 border-sage-200 p-4">
                    <h4 className="font-medium text-sage-900 mb-2">Loading States</h4>
                    <p className="text-sm text-sage-800">Linear for consistent progress</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Animation Patterns */}
      {activeTab === 'patterns' && (
        <div className="space-y-8">
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
            <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Animation Patterns</h2>
            <p className="text-charcoal-700 mb-6">
              Reusable animation patterns that can be applied consistently across all Peak Priorities touchpoints.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <PatternExample
                title="Staggered Entrance"
                description="Elements appearing in sequence"
                patternType="Stagger"
              >
                <div className="flex gap-2">
                  {Array.from({ length: 4 }, (_, i) => (
                    <div 
                      key={i}
                      className="w-6 h-6 bg-[var(--mustard)] rounded stagger-item"
                      style={{ animationDelay: `${i * 100}ms` }}
                    ></div>
                  ))}
                </div>
              </PatternExample>

              <PatternExample
                title="Wave Effect"
                description="Ripple-like motion across elements"
                patternType="Wave"
              >
                <div className="flex gap-1">
                  {Array.from({ length: 5 }, (_, i) => (
                    <div 
                      key={i}
                      className="w-2 h-8 bg-consulting-slate-blue-600 rounded wave-item"
                      style={{ animationDelay: `${i * 150}ms` }}
                    ></div>
                  ))}
                </div>
              </PatternExample>

              <PatternExample
                title="Pulse Effect"
                description="Breathing-like animation"
                patternType="Pulse"
              >
                <div className="w-12 h-12 bg-sage-600 rounded-full pulse-item"></div>
              </PatternExample>

              <PatternExample
                title="Slide Reveal"
                description="Content sliding into view"
                patternType="Slide"
              >
                <div className="w-16 h-8 bg-coral-600 rounded slide-reveal"></div>
              </PatternExample>
            </div>
          </div>

          {/* Pattern Guidelines */}
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
            <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Pattern Guidelines</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-charcoal-900 mb-4">When to Use Patterns</h3>
                <div className="space-y-4">
                  <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
                    <h4 className="font-medium text-[var(--mustard)] mb-2">Staggered Entrance</h4>
                    <p className="text-sm text-[var(--mustard)]">Card grids, list items, form sections</p>
                  </div>
                  
                  <div className="bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-200 p-4">
                    <h4 className="font-medium text-consulting-slate-blue-900 mb-2">Wave Effect</h4>
                    <p className="text-sm text-consulting-slate-blue-800">Loading states, progress indicators</p>
                  </div>
                  
                  <div className="bg-sage-50 border-2 border-sage-200 p-4">
                    <h4 className="font-medium text-sage-900 mb-2">Pulse Effect</h4>
                    <p className="text-sm text-sage-800">Active states, notifications, live data</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Pattern Best Practices</h3>
                <div className="space-y-4">
                  <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
                    <h4 className="font-medium text-[var(--mustard)] mb-2">Consistency</h4>
                    <p className="text-sm text-[var(--mustard)]">Use the same pattern for similar interactions</p>
                  </div>
                  
                  <div className="bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-200 p-4">
                    <h4 className="font-medium text-consulting-slate-blue-900 mb-2">Performance</h4>
                    <p className="text-sm text-consulting-slate-blue-800">Keep patterns lightweight and efficient</p>
                  </div>
                  
                  <div className="bg-sage-50 border-2 border-sage-200 p-4">
                    <h4 className="font-medium text-sage-900 mb-2">Accessibility</h4>
                    <p className="text-sm text-sage-800">Respect reduced motion preferences</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Accessibility Guidelines */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Accessibility Guidelines</h2>
        <p className="text-charcoal-700 mb-6">
          Motion should enhance the experience for all users while respecting individual preferences and needs.
        </p>
        
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Reduced Motion</h3>
            <div className="space-y-4">
              <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
                <h4 className="font-medium text-[var(--mustard)] mb-2">Respect Preferences</h4>
                <p className="text-sm text-[var(--mustard)]">Check for prefers-reduced-motion media query</p>
              </div>
              
              <div className="bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-200 p-4">
                <h4 className="font-medium text-consulting-slate-blue-900 mb-2">Provide Alternatives</h4>
                <p className="text-sm text-consulting-slate-blue-800">Offer static alternatives for essential information</p>
              </div>
              
              <div className="bg-sage-50 border-2 border-sage-200 p-4">
                <h4 className="font-medium text-sage-900 mb-2">Essential Motion Only</h4>
                <p className="text-sm text-sage-800">Keep only motion that conveys important information</p>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Motion Guidelines</h3>
            <div className="space-y-4">
              <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
                <h4 className="font-medium text-[var(--mustard)] mb-2">Duration Limits</h4>
                <p className="text-sm text-[var(--mustard)]">Keep animations under 5 seconds maximum</p>
              </div>
              
              <div className="bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-200 p-4">
                <h4 className="font-medium text-consulting-slate-blue-900 mb-2">Pause Controls</h4>
                <p className="text-sm text-consulting-slate-blue-800">Provide pause/play controls for long animations</p>
              </div>
              
              <div className="bg-sage-50 border-2 border-sage-200 p-4">
                <h4 className="font-medium text-sage-900 mb-2">No Seizure Risk</h4>
                <p className="text-sm text-sage-800">Avoid flashing or rapid color changes</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Implementation Guidelines */}
      <div className="bg-gradient-to-r from-amber-50 to-consulting-slate-blue-50 border-2 border-[var(--mustard)] p-8 text-center">
        <h3 className="text-2xl font-bold text-charcoal-900 mb-3">
          Implementation Guidelines
        </h3>
        <p className="text-charcoal-700 max-w-3xl mx-auto mb-6">
          Use these motion principles consistently across all Peak Priorities touchpoints. 
          Motion should always serve a purpose and enhance the user experience while respecting accessibility needs.
        </p>
        <div className="flex gap-4 justify-center flex-wrap">
          <button className="flex items-center gap-2 px-6 py-3 bg-[var(--mustard)] text-white font-semibold hover:bg-[var(--mustard)] transition-colors">
            <Download className="h-5 w-5" />
            Download Guidelines
          </button>
          <button className="flex items-center gap-2 px-6 py-3 bg-[var(--bg-raised)] text-charcoal-900 border-2 border-[var(--mustard)] font-semibold hover:bg-[var(--mustard-dim)] transition-colors">
            <Zap className="h-5 w-5" />
            View Examples
          </button>
        </div>
      </div>

      {/* CSS for animations */}
      <style jsx>{`
        .fade-in {
          opacity: 0;
          transition: opacity 300ms ease-out;
        }
        .fade-in.animate {
          opacity: 1;
        }

        .slide-up {
          transform: translateY(20px);
          opacity: 0;
          transition: all 400ms ease-out;
        }
        .slide-up.animate {
          transform: translateY(0);
          opacity: 1;
        }

        .scale-in {
          transform: scale(0.8);
          opacity: 0;
          transition: all 250ms ease-out;
        }
        .scale-in.animate {
          transform: scale(1);
          opacity: 1;
        }

        .rotate-in {
          transform: rotate(-180deg);
          opacity: 0;
          transition: all 500ms ease-out;
        }
        .rotate-in.animate {
          transform: rotate(0deg);
          opacity: 1;
        }

        .compass-rotate {
          transform: rotate(0deg);
          transition: transform 600ms ease-in-out;
        }
        .compass-rotate.animate {
          transform: rotate(360deg);
        }

        .compass-needle {
          transform: rotate(0deg);
          transition: transform 600ms ease-in-out;
        }
        .compass-rotate.animate .compass-needle {
          transform: rotate(360deg);
        }

        .mountain-ascent {
          transform: translateY(20px);
          opacity: 0;
          transition: all 800ms ease-out;
        }
        .mountain-ascent.animate {
          transform: translateY(0);
          opacity: 1;
        }

        .mountain-peak {
          transform: scaleY(0);
          transform-origin: bottom;
          transition: transform 800ms ease-out;
        }
        .mountain-ascent.animate .mountain-peak {
          transform: scaleY(1);
        }

        .trail-dots {
          opacity: 0.3;
        }
        .trail-dot {
          animation: trail-pulse 1000ms ease-in-out infinite;
        }
        .trail-dot:nth-child(1) { animation-delay: 0ms; }
        .trail-dot:nth-child(2) { animation-delay: 200ms; }
        .trail-dot:nth-child(3) { animation-delay: 400ms; }
        .trail-dot:nth-child(4) { animation-delay: 600ms; }

        @keyframes trail-pulse {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.2); }
        }

        .target-focus {
          transform: scale(0.8);
          opacity: 0;
          transition: all 300ms ease-out;
        }
        .target-focus.animate {
          transform: scale(1);
          opacity: 1;
        }

        .stagger-item {
          opacity: 0;
          transform: translateY(20px);
          animation: stagger-in 400ms ease-out forwards;
        }

        @keyframes stagger-in {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .wave-item {
          transform: scaleY(0.3);
          animation: wave 1000ms ease-in-out infinite;
        }

        @keyframes wave {
          0%, 100% { transform: scaleY(0.3); }
          50% { transform: scaleY(1); }
        }

        .pulse-item {
          animation: pulse 2000ms ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.1); opacity: 0.8; }
        }

        .slide-reveal {
          transform: translateX(-100%);
          animation: slide-reveal 600ms ease-out forwards;
        }

        @keyframes slide-reveal {
          to {
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
};




